import type { ToolDesign, ToolDesignEntry, Pagination } from "@keystone/core";
import {
  FIXTURE_PETS,
  FIXTURE_ORDERS,
  FIXTURE_BOOKS,
  FIXTURE_LOANS,
  findPet,
  findBook,
  type FixtureOrder,
  type FixturePet
} from "./fixtures.js";

/**
 * In-process tool-execution layer. Maps each tool (identified by its `operations`
 * field — the operationId of the OpenAPI source) to a deterministic handler that
 * reads from the fixtures.
 *
 * The simulation reflects the design's claims:
 *  - If a tool declares `pagination`, the runtime applies cursor pagination correctly.
 *  - If a tool omits pagination (naive baseline), it returns only the first page —
 *    that's the bug the eval surfaces.
 */

export type ToolRuntimeResult = unknown;

export class ToolRuntime {
  constructor(private readonly design: ToolDesign) {}

  call(toolName: string, args: Record<string, unknown>): ToolRuntimeResult {
    const tool = this.design.tools.find((t) => t.name === toolName);
    if (!tool) throw new Error(`unknown tool: ${toolName}`);
    const opId = tool.operations[0];
    switch (opId) {
      case "listPets":
        return listWithPagination(FIXTURE_PETS, args, tool);
      case "getPet":
        return findPet(String(args.petId)) ?? { error: "not found" };
      case "createPet":
        return { id: `pet-NEW`, ...args };
      case "updatePet":
        return { id: String(args.petId), updated: true, ...args };
      case "deletePet":
        return { deleted: true };
      case "listOrders":
        return listWithPagination(FIXTURE_ORDERS, args, tool, (o) =>
          typeof args.status === "string" ? o.status === args.status : true
        );
      case "createOrder":
        return { id: `order-NEW`, ...args, status: "pending" };
      case "chargeOrder":
        return { chargeId: `ch-NEW`, amountCents: 1000 };
      case "listBooks":
        return listWithPagination(FIXTURE_BOOKS, args, tool, (b) =>
          typeof args.subject === "string" ? b.subject === args.subject : true
        );
      case "searchBooks": {
        const q = typeof args.q === "string" ? args.q.toLowerCase() : "";
        return listWithPagination(
          FIXTURE_BOOKS,
          args,
          tool,
          (b) => q === "" || b.title.toLowerCase().includes(q) || b.author.toLowerCase().includes(q)
        );
      }
      case "getBook":
        return findBook(String(args.bookId)) ?? { error: "not found" };
      case "listLoans":
        return listWithPagination(FIXTURE_LOANS, args, tool, (l) =>
          typeof args.status === "string" ? l.status === args.status : true
        );
      case "createLoan":
        return { id: `loan-NEW`, ...args, status: "active" };
      case "returnLoan":
        return { id: String(args.loanId), status: "returned" };
      default:
        // Unknown operationId — emit a benign stub so drift-introduced operations
        // can still be exercised without breaking the runner.
        return { stub: true, op: opId, args };
    }
  }
}

function listWithPagination<T>(
  items: T[],
  args: Record<string, unknown>,
  tool: ToolDesignEntry,
  filter?: (it: T) => boolean
): { items: T[]; nextCursor: string | null; totalIfDeclared?: number } {
  const filtered = filter ? items.filter(filter) : items;
  // If the tool does NOT declare pagination, mimic a naive server that just returns the first page.
  if (!tool.pagination) {
    const slice = filtered.slice(0, 20);
    return { items: slice, nextCursor: null };
  }
  const pag: Pagination = tool.pagination;
  const limitName = pag.paramNames.limit ?? "limit";
  const cursorName = pag.paramNames.cursor ?? pag.paramNames.page ?? pag.paramNames.offset ?? "cursor";
  const limitArg = Number(args[limitName] ?? pag.defaultLimit);
  const limit = Math.min(limitArg, pag.maxLimit);
  const cursorArg = args[cursorName];
  let start = 0;
  if (pag.kind === "cursor" && typeof cursorArg === "string" && cursorArg.length > 0) {
    start = Number(cursorArg.replace(/^cur-/, ""));
    if (!Number.isFinite(start) || start < 0) start = 0;
  } else if (pag.kind === "page") {
    const page = Math.max(1, Number(cursorArg ?? 1));
    start = (page - 1) * limit;
  } else if (pag.kind === "offset") {
    start = Math.max(0, Number(cursorArg ?? 0));
  }
  const end = start + limit;
  const slice = filtered.slice(start, end);
  const nextCursor = end < filtered.length ? `cur-${end}` : null;
  return { items: slice, nextCursor };
}

export type { FixturePet, FixtureOrder };
export { FIXTURE_PETS, FIXTURE_ORDERS };
